import type { CompanySummary, CreateEventInput, Event } from "@mvk/shared";
import { isApiEnabled } from "../api/config";
import { apiRequest } from "../api/client";

export interface CreateExhibitorPayload {
  name: string;
  category: string;
  shortAbout?: string;
  booth?: string;
  hall?: string;
  phone?: string;
  email?: string;
  website?: string;
  country?: string;
  city?: string;
  contactPersonTitle?: string;
}

export interface VisitorAccessRow {
  userId: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  position?: string | null;
  grantedAt: string;
  source: string;
  ticketNumber?: string | null;
  ticketId?: string | null;
  qrPayload?: string | null;
  roles?: Array<"visitor" | "exhibitor">;
  companyId?: string | null;
  companyName?: string | null;
  /** Hand-created test access — edit/delete allowed. */
  manualTest?: boolean;
  barcodeOrigin?: "replica" | "generated" | null;
  authorized?: boolean;
  lastSeenAt?: string | null;
  /** When the row first appeared in our PostgreSQL. */
  addedAt?: string;
}

export type AccessRoleFilter = "all" | "visitor" | "exhibitor";
export type AccessAuthorizedFilter = "all" | "yes" | "no";

export interface ListVisitorAccessResult {
  visitors: VisitorAccessRow[];
  total: number;
  /** Ever opened the app (last_seen_at). Same filters as total — not page-local. */
  authorizedTotal: number;
  /** last_seen within ~15 min. Same filters as total. */
  onlineNow: number;
  page: number;
  pageSize: number;
}

export interface GrantExhibitorAccessResponse {
  userId: string;
  phone: string;
  email: string | null;
  login: string;
  temporaryPassword?: string;
  companyId: string;
  companyName: string;
  alsoVisitor: boolean;
  fullName: string;
  position: string;
  ticketNumber: string | null;
  badgeQrCode: string | null;
  loginHint: string;
}

export const adminEventsApi = {
  getEvent(eventId: string): Promise<{ event: Event }> {
    return apiRequest(`/events/${eventId}`, { audience: "admin" });
  },

  updateEvent(eventId: string, patch: Partial<CreateEventInput>) {
    return apiRequest<{ event: Event }>(`/events/${eventId}`, {
      method: "PATCH",
      body: patch,
      audience: "admin",
    });
  },

  listExhibitors(eventId: string): Promise<{ exhibitors: CompanySummary[] }> {
    return apiRequest(`/events/${eventId}/exhibitors`, { audience: "admin" });
  },

  createExhibitor(eventId: string, payload: CreateExhibitorPayload) {
    return apiRequest<{ exhibitors: CompanySummary[] }>(`/events/${eventId}/exhibitors`, {
      method: "POST",
      body: payload,
      audience: "admin",
    });
  },

  deleteExhibitor(eventId: string, companyId: string) {
    return apiRequest<{ ok: boolean }>(`/events/${eventId}/exhibitors/${companyId}`, {
      method: "DELETE",
      audience: "admin",
    });
  },

  listVisitorAccess(
    eventId: string,
    opts?: {
      q?: string;
      role?: AccessRoleFilter;
      authorized?: AccessAuthorizedFilter;
      page?: number;
      pageSize?: number;
    },
  ): Promise<ListVisitorAccessResult> {
    const params = new URLSearchParams();
    if (opts?.q?.trim()) params.set("q", opts.q.trim());
    if (opts?.role && opts.role !== "all") params.set("role", opts.role);
    if (opts?.authorized && opts.authorized !== "all") params.set("authorized", opts.authorized);
    if (opts?.page) params.set("page", String(opts.page));
    if (opts?.pageSize) params.set("pageSize", String(opts.pageSize));
    const qs = params.toString();
    return apiRequest(`/events/${eventId}/visitor-access${qs ? `?${qs}` : ""}`, { audience: "admin" });
  },

  grantVisitorAccess(eventId: string, payload: { phone?: string; userId?: string }) {
    return apiRequest<{ ok: boolean; userId: string }>(`/events/${eventId}/visitor-access`, {
      method: "POST",
      body: payload,
      audience: "admin",
    });
  },

  grantExhibitorAccess(
    eventId: string,
    payload: {
      phone: string;
      companyId: string;
      fullName: string;
      position?: string;
      alsoVisitor?: boolean;
    },
  ) {
    return apiRequest<{ ok: boolean; access: GrantExhibitorAccessResponse }>(
      `/events/${eventId}/exhibitor-access`,
      {
        method: "POST",
        body: payload,
        audience: "admin",
      },
    );
  },

  updateManualAccess(
    eventId: string,
    userId: string,
    payload: {
      fullName?: string;
      position?: string;
      phone?: string;
      companyId?: string | null;
    },
  ) {
    return apiRequest<{ ok: boolean; visitor: VisitorAccessRow }>(
      `/events/${eventId}/access/${userId}`,
      {
        method: "PATCH",
        body: payload,
        audience: "admin",
      },
    );
  },

  deleteManualAccess(eventId: string, userId: string) {
    return apiRequest<{ ok: boolean }>(`/events/${eventId}/access/${userId}`, {
      method: "DELETE",
      audience: "admin",
    });
  },

  uploadHero(eventId: string, heroImage: string) {
    return apiRequest<{ event: Event; writtenToTezam: boolean }>(`/events/${eventId}/hero`, {
      method: "PUT",
      body: { heroImage },
      audience: "admin",
    });
  },
};

export async function tryLoadExhibitors(eventId: string): Promise<CompanySummary[] | null> {
  if (!isApiEnabled()) return null;
  const data = await adminEventsApi.listExhibitors(eventId);
  return data.exhibitors;
}
